import { FormControl, FormControlLabel, Grid, RadioGroup, useEventCallback } from '@mui/material';
import {
  AccordionGeneralityForm,
  ForeignForm,
} from 'civil-status/view-models/accordion-generality-form';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { Radio } from '@mui/material';
import Foreign from '../../../shared/foreign';
import Italian from '../../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/lab';
import { IdResidence } from 'civil-status/constants/id-residence';
import { Checkbox } from '@mui/material';

interface FormGeneralityBaseProps {
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  nothingRequired?: boolean;
  configuration?: string;
  dateFormationAct?: string;
}

const FormGeneralityBaseComponent = ({
  disabled = false,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  nothingRequired = false,
  configuration,
  dateFormationAct,
}: FormGeneralityBaseProps): React.ReactElement => {
  const isBaseAvv2: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEAVV2;
  }, [configuration]);

  const isBaseAvv3: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEAVV3;
  }, [configuration]);

  const isBaseAvv4: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASEAVV4;
  }, [configuration]);

  const onChangeResidence = useEventCallback((typeResidence: IdResidence) => {
    onChangeFieldsGenerality({
      typeResidence,
      italianResidence: {
        location: '',
        province: '',
        municipality: null,
      },
      foreignResidence: {
        state: null,
        province: '',
        municipality: null,
      },
      address: '',
    });
  });

  const onFieldItalianResidenceChange = useCallback(
    (key, value) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianResidenceChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignResidenceChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreignResidence', {
        ...(generality || {}).foreignResidence,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsForeignResidenceChange = useCallback(
    (value) => {
      onChangeGenerality('foreignResidence', {
        ...(value || {}),
      });
    },
    [generality, onChangeGenerality]
  );

  return (
    <Grid container spacing={2} item>
      {isBaseAvv3 || isBaseAvv4 ? undefined : (
        <Fragment>
          <Grid item xs={12} md={9}>
            <PmiTextField
              label={`Cognome ${nothingRequired ? '' : '*'}`}
              aria-label="Cognome"
              id="surname"
              fullWidth
              disabled={disabled}
              helperText={errorsGenerality?.surname}
              error={touchedGenerality?.surname && Boolean(errorsGenerality?.surname)}
              value={generality?.surname || ''}
              inputProps={{
                maxLength: '80',
              }}
              onChange={(event) => {
                onChangeGenerality('surname', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <PmiTextField
              label={`Nome ${nothingRequired ? '' : '*'}`}
              aria-label="Nome"
              id="name"
              fullWidth
              disabled={disabled}
              inputProps={{
                maxLength: '80',
              }}
              helperText={errorsGenerality?.name}
              error={touchedGenerality?.name && Boolean(errorsGenerality?.name)}
              value={generality?.name || ''}
              onChange={(event) => {
                onChangeGenerality('name', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <PmiTextField
              label={`Foro di appartenenza ${nothingRequired ? '' : '*'}`}
              aria-label="Foro di appartenenza"
              id="lawyerForum"
              fullWidth
              disabled={disabled}
              inputProps={{
                maxLength: '80',
              }}
              helperText={errorsGenerality?.lawyerForum}
              error={touchedGenerality?.lawyerForum && Boolean(errorsGenerality?.lawyerForum)}
              value={generality?.lawyerForum || ''}
              onChange={(event) => {
                onChangeGenerality('lawyerForum', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      )}
      {isBaseAvv2 ? (
        <Fragment>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Residenza"
                id="typeResidence"
                value={generality?.typeResidence || IdResidence.Italian}
              >
                <FormControlLabel
                  value={IdResidence.Italian}
                  control={<Radio />}
                  label="Residenza Italia"
                  disabled={disabled}
                  onChange={() => onChangeResidence(IdResidence.Italian)}
                />
                <FormControlLabel
                  value={IdResidence.Foreign}
                  control={<Radio />}
                  label="Residenza Estero"
                  disabled={disabled}
                  onChange={() => onChangeResidence(IdResidence.Foreign)}
                />
                <FormControlLabel
                  value={IdResidence.NotKnown}
                  control={<Radio />}
                  label="Residenza non nota"
                  disabled={disabled}
                  onChange={() => onChangeResidence(IdResidence.NotKnown)}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {generality?.typeResidence === IdResidence.Foreign ? (
            <Italian
              disabledComponent={disabled}
              values={generality?.italianResidence}
              onChange={onFieldItalianResidenceChange}
              onChanges={onFieldsItalianResidenceChange}
              fromDate={dateFormationAct ? DateTime.fromISO(dateFormationAct) : null}
              errors={errorsGenerality?.italianResidence as FormikErrors<ItalianForm>}
              touched={touchedGenerality?.italianResidence as unknown as FormikTouched<ItalianForm>}
              labelMunicipality="Comune di residenza"
              labelProvince="Provincia di residenza"
              required={!nothingRequired}
              isResidence
            />
          ) : undefined}
          {generality?.typeResidence === IdResidence.Italian ? (
            <Foreign
              disabledComponent={disabled}
              values={generality?.foreignResidence}
              onChange={onFieldForeignResidenceChange}
              onChanges={onFieldsForeignResidenceChange}
              errors={errorsGenerality?.foreignResidence as FormikErrors<ForeignForm>}
              touched={touchedGenerality?.foreignResidence as unknown as FormikTouched<ForeignForm>}
              labelState="Stato estero di residenza"
              required={!nothingRequired}
              isResidence
            />
          ) : undefined}
          {generality?.typeResidence === IdResidence.Italian ||
          generality?.typeResidence === IdResidence.Foreign ? (
            <Grid item xs={12}>
              <PmiTextField
                aria-label="Indirizzo di residenza"
                label="Indirizzo di residenza"
                id="address"
                name="address"
                fullWidth
                disabled={disabled}
                helperText={errorsGenerality?.address}
                error={touchedGenerality?.address && Boolean(errorsGenerality?.address)}
                type="text"
                variant="outlined"
                value={generality?.address || ''}
                onChange={(event) => {
                  onChangeGenerality('address', event.target.value);
                }}
                inputProps={{
                  maxLength: '240',
                }}
              />
            </Grid>
          ) : undefined}
          {generality?.typeResidence === IdResidence.NotKnown ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="isUntraceable"
                    checked={generality.isUntraceable || false}
                    onChange={() => {
                      onChangeGenerality('isUntraceable', !generality.isUntraceable);
                    }}
                    inputProps={{ 'aria-label': 'Irreperibile' }}
                  />
                }
                label="Irreperibile"
              />
            </Grid>
          ) : undefined}
        </Fragment>
      ) : undefined}
      {isBaseAvv3 ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label={`Data comunicazione ${
                nothingRequired || !generality?.isRequiredFromOtherAccordion ? '' : '*'
              }`}
              aria-label="Data comunicazione"
              value={generality?.dateCommunication || null}
              onChange={(date) => {
                onChangeGenerality('dateCommunication', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateCommunication"
                  fullWidth
                  helperText={errorsGenerality?.dateCommunication}
                  error={
                    touchedGenerality?.dateCommunication &&
                    Boolean(errorsGenerality?.dateCommunication)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label={`Protocollo ${
                nothingRequired || !generality?.isRequiredFromOtherAccordion ? '' : '*'
              }`}
              aria-label="Protocollo"
              id="protocol"
              name="protocol"
              fullWidth
              type="text"
              variant="outlined"
              value={generality?.protocol || ''}
              onChange={(event) => {
                onChangeGenerality('protocol', event.target.value);
              }}
              error={touchedGenerality?.protocol && Boolean(errorsGenerality?.protocol)}
              helperText={errorsGenerality?.protocol}
              disabled={disabled}
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityBaseComponent.displayName = 'Form generality BASE - BaseAvv component';

const FormBaseAvv = memo(FormGeneralityBaseComponent);

export default FormBaseAvv;
